// src/components/CustomerMenu.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useReactToPrint } from 'react-to-print';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com/api";
const stallID = localStorage.getItem("stallID") || "mummy";

export default function CustomerMenu() {
  const [foods, setFoods] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableNumber, setTableNumber] = useState("");
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);
  const [showAddonsModal, setShowAddonsModal] = useState(false);

  const printRef = useRef();

  useEffect(() => {
    const fetchFoods = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/admin/foods?stallID=${stallID}`);
        setFoods(res.data);
      } catch (err) {
        console.error("Fetch foods error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFoods();
  }, []);

  const addToCart = (food) => {
    // If food has addons, show modal to select them
    if (food.addons && food.addons.length > 0) {
      setSelectedFood(food);
      setShowAddonsModal(true);
    } else {
      // Add without addons
      const existing = cart.find((c) =>
        c.id === food.id &&
        (!c.selectedAddons || c.selectedAddons.length === 0) &&
        (!c.notes || c.notes === "")
      );

      if (existing) {
        setCart(cart.map((c) =>
          c.id === food.id &&
          (!c.selectedAddons || c.selectedAddons.length === 0) &&
          (!c.notes || c.notes === "")
            ? { ...c, quantity: c.quantity + 1 }
            : c
        ));
      } else {
        setCart([...cart, {
          ...food,
          quantity: 1,
          selectedAddons: [],
          notes: "",
          addonsTotal: 0
        }]);
      }
    }
  };

  const handleAddWithAddons = (selectedAddons, notes) => {
    if (!selectedFood) return;

    const addonsTotal = selectedAddons.reduce((total, addon) =>
      total + (addon.price * addon.quantity), 0
    );

    const existing = cart.find((c) =>
      c.id === selectedFood.id &&
      JSON.stringify(c.selectedAddons) === JSON.stringify(selectedAddons) &&
      c.notes === notes
    );

    if (existing) {
      setCart(cart.map((c) =>
        c.id === selectedFood.id &&
        JSON.stringify(c.selectedAddons) === JSON.stringify(selectedAddons) &&
        c.notes === notes
          ? { ...c, quantity: c.quantity + 1, addonsTotal }
          : c
      ));
    } else {
      setCart([...cart, {
        ...selectedFood,
        quantity: 1,
        selectedAddons,
        notes,
        addonsTotal
      }]);
    }

    setShowAddonsModal(false);
    setSelectedFood(null);
  };

  const handleQuantityChange = (foodId, delta) => {
    setCart(cart.map((c) => {
      if (c.id === foodId) {
        const newQuantity = Math.max(1, c.quantity + delta);
        return { ...c, quantity: newQuantity };
      }
      return c;
    }));
  };

  const handleAddonQuantityChange = (foodId, addonIndex, delta) => {
    setCart(cart.map((c) => {
      if (c.id === foodId) {
        const updatedAddons = [...c.selectedAddons];
        const newQuantity = Math.max(0, updatedAddons[addonIndex].quantity + delta);

        if (newQuantity === 0) {
          updatedAddons.splice(addonIndex, 1);
        } else {
          updatedAddons[addonIndex].quantity = newQuantity;
        }

        const addonsTotal = updatedAddons.reduce((total, addon) =>
          total + (addon.price * addon.quantity), 0
        );

        return { ...c, selectedAddons: updatedAddons, addonsTotal };
      }
      return c;
    }));
  };

  const removeFromCart = (foodId) => {
    setCart(cart.filter((c) => c.id !== foodId));
  };

  const updateNotes = (foodId, notes) => {
    setCart(cart.map((c) =>
      c.id === foodId ? { ...c, notes } : c
    ));
  };

  const calculateItemTotal = (item) => {
    const itemPrice = item.finalPrice || item.price || 0;
    const addonsTotal = item.addonsTotal || 0;
    return (itemPrice * item.quantity) + addonsTotal;
  };

  const calculateCartTotal = () => {
    return cart.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  const handlePlaceOrder = async () => {
    if (!tableNumber) {
      alert("Please enter your table number before placing order.");
      return;
    }

    if (cart.length === 0) {
      alert("Your cart is empty!");
      return;
    }

    try {
      const orderData = {
        stallID,
        tableNumber,
        items: cart.map((item) => ({
          id: item.id,
          foodName: item.foodName,
          quantity: item.quantity,
          price: item.finalPrice || item.price,
          notes: item.notes,
          selectedAddons: item.selectedAddons || [],
          addonsTotal: item.addonsTotal || 0,
          itemTotal: calculateItemTotal(item)
        })),
        totalAmount: calculateCartTotal(),
        orderTime: new Date().toLocaleString('en-MY')
      };

      const res = await axios.post(`${SERVER_URL}/api/admin/orders`, orderData);
      if (res.data.success) {
        // Set current order for confirmation and printing
        const orderWithId = {
          ...orderData,
          orderId: res.data.orderId || Date.now()
        };
        setCurrentOrder(orderWithId);
        setShowOrderConfirmation(true);
        setCart([]);
      } else {
        alert("Order submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Order submission error:", err);
      alert("Error sending order to kitchen.");
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @media print {
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          margin: 0;
          padding: 5px;
        }
        .no-print { display: none !important; }
        .kitchen-ticket {
          width: 80mm;
          border: 1px solid #000;
          padding: 8px;
          margin: 0 auto;
          background: white;
        }
        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    documentTitle: `Kitchen-Order-${currentOrder?.orderId || ''}`
  });

  const handleBackToMenu = () => {
    setShowOrderConfirmation(false);
    setCurrentOrder(null);
  };

  if (loading) return <div>Loading menu...</div>;

  if (showOrderConfirmation && currentOrder) {
    return (
      <OrderConfirmation
        order={currentOrder}
        onBack={handleBackToMenu}
        onPrint={handlePrint}
        printRef={printRef}
      />
    );
  }

  return (
    <div style={{ padding: "10px", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>Menu - {stallID}</h2>

      {/* Table Number Input */}
      <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px" }}>
        <label><strong>Table Number: </strong></label>
        <input
          type="text"
          value={tableNumber}
          onChange={(e) => setTableNumber(e.target.value)}
          placeholder="e.g. T3"
          style={{ padding: "5px", marginLeft: "10px" }}
        />
      </div>

      {/* Food Menu */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: "15px",
        marginBottom: "30px"
      }}>
        {foods.map((food) => (
          <div key={food.id} style={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "10px",
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <img
              src={`${SERVER_URL}/food-images/${food.image || "placeholder.png"}`}
              alt={food.foodName}
              style={{
                width: "100%",
                height: "120px",
                objectFit: "cover",
                borderRadius: "5px",
                marginBottom: "10px"
              }}
            />
            <div style={{ fontWeight: "bold", marginBottom: "5px" }}>{food.foodName}</div>
            <div style={{ color: "#28a745", fontWeight: "bold", marginBottom: "10px" }}>
              RM {(food.finalPrice || food.price)?.toFixed(2)}
            </div>
            <button
              onClick={() => addToCart(food)}
              style={{
                width: "100%",
                padding: "8px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer"
              }}
            >
              Add to Cart
            </button>
          </div>
        ))}
      </div>

      {/* Cart Section */}
      <div style={{
        backgroundColor: "#f8f9fa",
        padding: "20px",
        borderRadius: "10px",
        border: "1px solid #ddd"
      }}>
        <h2>Your Cart {tableNumber && `- Table ${tableNumber}`}</h2>
        {cart.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            Your cart is empty. Add some items from the menu above.
          </div>
        ) : (
          <div>
            {cart.map((item, index) => (
              <CartItem
                key={`${item.id}-${index}`}
                item={item}
                index={index}
                onQuantityChange={handleQuantityChange}
                onAddonQuantityChange={handleAddonQuantityChange}
                onRemove={removeFromCart}
                onUpdateNotes={updateNotes}
              />
            ))}

            <div style={{
              marginTop: "20px",
              paddingTop: "15px",
              borderTop: "2px solid #ddd",
              textAlign: "right"
            }}>
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                TOTAL: RM {calculateCartTotal().toFixed(2)}
              </div>
            </div>

            {/* Place Order Button */}
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                onClick={handlePlaceOrder}
                style={{
                  backgroundColor: "#28a745",
                  color: "white",
                  padding: "12px 30px",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                🍽️ Place Order
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Addons Modal */}
      {showAddonsModal && selectedFood && (
        <AddonsModal
          food={selectedFood}
          onClose={() => {
            setShowAddonsModal(false);
            setSelectedFood(null);
          }}
          onConfirm={handleAddWithAddons}
        />
      )}
    </div>
  );
}

// Cart Item Component
const CartItem = ({ item, index, onQuantityChange, onAddonQuantityChange, onRemove, onUpdateNotes }) => {
  const [showNotes, setShowNotes] = useState(false);

  return (
    <div style={{
      marginBottom: "15px",
      padding: "15px",
      backgroundColor: "white",
      borderRadius: "8px",
      border: "1px solid #ddd"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: "16px" }}>{item.foodName}</strong>
          <div style={{ color: "#28a745", fontWeight: "bold" }}>
            RM {(item.finalPrice || item.price)?.toFixed(2)} × {item.quantity} = RM {((item.finalPrice || item.price) * item.quantity).toFixed(2)}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            onClick={() => onQuantityChange(item.id, -1)}
            style={{ padding: "5px 10px", fontSize: "16px" }}
          >-</button>
          <span style={{ minWidth: "30px", textAlign: "center" }}>{item.quantity}</span>
          <button
            onClick={() => onQuantityChange(item.id, 1)}
            style={{ padding: "5px 10px", fontSize: "16px" }}
          >+</button>
          <button
            onClick={() => onRemove(item.id)}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "3px",
              padding: "5px 10px",
              marginLeft: "10px"
            }}
          >
            Remove
          </button>
        </div>
      </div>

      {/* Addons */}
      {item.selectedAddons && item.selectedAddons.length > 0 && (
        <div style={{ marginLeft: "20px", marginBottom: "10px" }}>
          <strong>Addons:</strong>
          {item.selectedAddons.map((addon, addonIndex) => (
            <div key={addon.id} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "5px 0"
            }}>
              <span>{addon.name} (+RM {addon.price.toFixed(2)})</span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  onClick={() => onAddonQuantityChange(item.id, addonIndex, -1)}
                  style={{ padding: "2px 6px", fontSize: "12px" }}
                >-</button>
                <span style={{ minWidth: "20px", textAlign: "center" }}>{addon.quantity}</span>
                <button
                  onClick={() => onAddonQuantityChange(item.id, addonIndex, 1)}
                  style={{ padding: "2px 6px", fontSize: "12px" }}
                >+</button>
                <span style={{ marginLeft: "10px", fontWeight: "bold" }}>
                  RM {(addon.price * addon.quantity).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
          <div style={{ fontWeight: "bold", textAlign: "right", marginTop: "5px" }}>
            Addons Total: RM {item.addonsTotal?.toFixed(2) || "0.00"}
          </div>
        </div>
      )}

      {/* Notes */}
      <div style={{ marginTop: "10px" }}>
        <button
          onClick={() => setShowNotes(!showNotes)}
          style={{
            backgroundColor: "transparent",
            border: "none",
            color: "#007bff",
            cursor: "pointer",
            fontSize: "12px"
          }}
        >
          {showNotes ? "▼" : "▶"} Add Special Instructions
        </button>
        {showNotes && (
          <textarea
            value={item.notes || ""}
            onChange={(e) => onUpdateNotes(item.id, e.target.value)}
            placeholder="Any special requests or instructions..."
            style={{
              width: "100%",
              padding: "8px",
              marginTop: "5px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              resize: "vertical",
              minHeight: "60px"
            }}
          />
        )}
      </div>

      {/* Item Total */}
      <div style={{
        textAlign: "right",
        marginTop: "10px",
        paddingTop: "10px",
        borderTop: "1px solid #eee",
        fontWeight: "bold"
      }}>
        Item Total: RM {((item.finalPrice || item.price) * item.quantity + (item.addonsTotal || 0)).toFixed(2)}
      </div>
    </div>
  );
};

// Addons Modal Component
const AddonsModal = ({ food, onClose, onConfirm }) => {
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [notes, setNotes] = useState("");

  const handleAddonToggle = (addon) => {
    const existingIndex = selectedAddons.findIndex(a => a.id === addon.id);
    if (existingIndex > -1) {
      const updated = [...selectedAddons];
      updated.splice(existingIndex, 1);
      setSelectedAddons(updated);
    } else {
      setSelectedAddons([...selectedAddons, { ...addon, quantity: 1 }]);
    }
  };

  const handleAddonQuantityChange = (addonId, delta) => {
    setSelectedAddons(selectedAddons.map(addon =>
      addon.id === addonId
        ? { ...addon, quantity: Math.max(1, addon.quantity + delta) }
        : addon
    ));
  };

  const handleConfirm = () => {
    onConfirm(selectedAddons, notes);
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "10px",
        width: "90%",
        maxWidth: "500px",
        maxHeight: "80vh",
        overflow: "auto"
      }}>
        <h3>Customize {food.foodName}</h3>

        {/* Addons Selection */}
        {food.addons && food.addons.length > 0 && (
          <div style={{ marginBottom: "15px" }}>
            <h4>Addons (Optional):</h4>
            {food.addons.map(addon => (
              <div key={addon.id} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                marginBottom: "5px"
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={selectedAddons.some(a => a.id === addon.id)}
                    onChange={() => handleAddonToggle(addon)}
                  />
                  <span>{addon.name} (+RM {addon.price.toFixed(2)})</span>
                </label>

                {selectedAddons.some(a => a.id === addon.id) && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                      onClick={() => handleAddonQuantityChange(addon.id, -1)}
                      style={{ padding: "2px 6px" }}
                    >-</button>
                    <span style={{ minWidth: "20px", textAlign: "center" }}>
                      {selectedAddons.find(a => a.id === addon.id)?.quantity || 1}
                    </span>
                    <button
                      onClick={() => handleAddonQuantityChange(addon.id, 1)}
                      style={{ padding: "2px 6px" }}
                    >+</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Special Instructions */}
        <div style={{ marginBottom: "20px" }}>
          <h4>Special Instructions:</h4>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special requests or dietary requirements..."
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              resize: "vertical",
              minHeight: "80px"
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: "8px 16px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

// Order Confirmation Component
const OrderConfirmation = ({ order, onBack, onPrint, printRef }) => {
  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <div className="no-print" style={{ marginBottom: "20px" }}>
        <button
          onClick={onBack}
          style={{
            padding: "10px 20px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginRight: "10px"
          }}
        >
          ← Back to Menu
        </button>
        <button
          onClick={onPrint}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer"
          }}
        >
          🖨️ Print Kitchen Ticket
        </button>
      </div>

      {/* Kitchen Ticket for Printing */}
      <div ref={printRef} className="kitchen-ticket">
        <div style={{ textAlign: "center", marginBottom: "10px", borderBottom: "1px solid #000", paddingBottom: "8px" }}>
          <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>{order.stallID.toUpperCase()} KITCHEN</h2>
          <p style={{ margin: "2px 0", fontSize: "10px" }}>ORDER TICKET</p>
        </div>

        <div style={{ marginBottom: "8px", borderBottom: "1px dashed #000", paddingBottom: "8px" }}>
          <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Order #:</strong> {order.orderId}</p>
          <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Table #:</strong> {order.tableNumber}</p>
          <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Time:</strong> {order.orderTime}</p>
        </div>

        <div style={{ marginBottom: "8px" }}>
          {order.items.map((item, index) => (
            <div key={index} style={{ marginBottom: "10px", borderBottom: "1px dotted #000", paddingBottom: "5px" }}>
              <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
                {item.quantity} x {item.foodName}
              </p>

              {item.notes && (
                <p style={{ margin: "2px 0", fontSize: "9px", fontStyle: "italic" }}>
                  ** {item.notes}
                </p>
              )}

              {item.selectedAddons && item.selectedAddons.length > 0 && (
                <div style={{ marginLeft: "8px" }}>
                  {item.selectedAddons.map((addon, addonIndex) => (
                    <p key={addonIndex} style={{ margin: "1px 0", fontSize: "9px" }}>
                      + {addon.quantity} x {addon.name}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ borderTop: "2px solid #000", paddingTop: "8px", textAlign: "center" }}>
          <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
            TOTAL: RM {order.totalAmount.toFixed(2)}
          </p>
        </div>

        <div style={{ textAlign: "center", marginTop: "10px", fontSize: "8px" }}>
          <p>*** KITCHEN COPY ***</p>
          <p>Thank you!</p>
        </div>
      </div>

      {/* Screen Confirmation */}
      <div className="no-print" style={{
        marginTop: "30px",
        padding: "20px",
        backgroundColor: "#d4edda",
        borderRadius: "10px",
        border: "1px solid #c3e6cb"
      }}>
        <h2 style={{ color: "#155724", marginBottom: "15px" }}>✅ Order Placed Successfully!</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div><strong>Order ID:</strong> {order.orderId}</div>
          <div><strong>Table Number:</strong> {order.tableNumber}</div>
          <div><strong>Total Amount:</strong> RM {order.totalAmount.toFixed(2)}</div>
          <div><strong>Order Time:</strong> {order.orderTime}</div>
        </div>

        <div style={{ marginTop: "20px" }}>
          <h3 style={{ color: "#155724" }}>Order Summary:</h3>
          {order.items.map((item, index) => (
            <div key={index} style={{
              marginBottom: "10px",
              padding: "10px",
              backgroundColor: "white",
              borderRadius: "5px"
            }}>
              <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
                {item.quantity}x {item.foodName} - RM {((item.price || item.finalPrice) * item.quantity).toFixed(2)}
              </p>
              {item.selectedAddons && item.selectedAddons.length > 0 && (
                <div style={{ marginLeft: "15px" }}>
                  {item.selectedAddons.map((addon, addonIndex) => (
                    <p key={addonIndex} style={{ margin: "2px 0", fontSize: "14px", color: "#666" }}>
                      + {addon.quantity}x {addon.name} (+RM {(addon.price * addon.quantity).toFixed(2)})
                    </p>
                  ))}
                </div>
              )}
              {item.notes && (
                <p style={{ margin: "5px 0 0 0", fontStyle: "italic", color: "#666", fontSize: "14px" }}>
                  Note: {item.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
